import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../../assets/styles/components/GeoSelector.module.scss';
import { AreaTargetType } from '../../types/project';

interface GeoTarget {
  id: string;
  name: string;
  label: string;
  parent_id?: string;
  target_type: string;
}

export interface GeoSelectorProps {
  geoTargets: GeoTarget[];
  onAdd: (areaId: string, type: AreaTargetType) => void;
}

export const getFullAreaName = (geo: GeoTarget, allTargets: GeoTarget[], forDropdown = false) => {
  let baseName = geo.label || geo.name;
  let prefName = '';

  if (geo.parent_id) {
    const parent = allTargets.find((item) => item.id === geo.parent_id);
    if (parent) {
      prefName = parent.label || parent.name;
    }
  }

  const tokyoWards = ["千代田", "中央", "港", "新宿", "文京", "台東", "墨田", "江東", "品川", "目黒", "大田", "世田谷", "渋谷", "中野", "杉並", "豊島", "北", "荒川", "板橋", "練馬", "足立", "葛飾", "江戸川"];

  if (geo.target_type === 'City') {
    if ((prefName === '東京都' || prefName === 'Tokyo') && tokyoWards.includes(baseName)) {
      baseName += '区';
    } else if (!baseName.match(/[市区町村]$/)) {
      baseName += '市';
    }
  } else if (geo.target_type === 'Ward') {
    if (!baseName.match(/[区]$/)) {
      baseName += '区';
    }
  }

  if (geo.target_type === 'Prefecture') {
    return baseName;
  }

  if (forDropdown) {
    return baseName;
  }

  return prefName + baseName;
};

export const GeoSelector: React.FC<GeoSelectorProps> = ({ geoTargets, onAdd }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPrefecture, setSelectedPrefecture] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [targetType, setTargetType] = useState<AreaTargetType>('INCLUDE');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const prefectures = useMemo(() => {
    return geoTargets.filter(g => g.target_type === 'Prefecture');
  }, [geoTargets]);

  const cities = useMemo(() => {
    let filtered = geoTargets.filter(g => g.target_type === 'City' || g.target_type === 'Ward');
    if (selectedPrefecture) {
      filtered = filtered.filter(g => g.parent_id === selectedPrefecture);
    }
    return filtered;
  }, [geoTargets, selectedPrefecture]);

  const displayPrefectures = useMemo(() => {
    if (!searchQuery) return prefectures;
    const lowerQ = searchQuery.toLowerCase();
    return prefectures.filter(p =>
      p.label.toLowerCase().includes(lowerQ) ||
      p.name.toLowerCase().includes(lowerQ)
    );
  }, [prefectures, searchQuery]);

  const displayCities = useMemo(() => {
    if (!searchQuery) return cities;
    const lowerQ = searchQuery.toLowerCase();
    return cities.filter(c =>
      c.label.toLowerCase().includes(lowerQ) ||
      c.name.toLowerCase().includes(lowerQ)
    );
  }, [cities, searchQuery]);

  const rankedSearchResults = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return [];
    }

    return geoTargets
      .map((geo) => {
        const label = geo.label.toLowerCase();
        const name = geo.name.toLowerCase();
        let score = 0;

        if (label === normalizedQuery || name === normalizedQuery) {
          score += 100;
        }
        if (label.startsWith(normalizedQuery) || name.startsWith(normalizedQuery)) {
          score += 50;
        }
        if (label.includes(normalizedQuery)) {
          score += 20;
        }
        if (name.includes(normalizedQuery)) {
          score += 10;
        }
        if (selectedPrefecture && geo.parent_id === selectedPrefecture) {
          score += 5;
        }

        return { geo, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        if (a.geo.target_type !== b.geo.target_type) {
          return a.geo.target_type === 'Prefecture' ? -1 : 1;
        }
        return getFullAreaName(a.geo, geoTargets, false).localeCompare(
          getFullAreaName(b.geo, geoTargets, false),
          'ja'
        );
      })
      .slice(0, 20)
      .map((item) => item.geo);
  }, [geoTargets, searchQuery, selectedPrefecture]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAdd = () => {
    const targetId = selectedCity || selectedPrefecture;
    if (!targetId) return;
    onAdd(targetId, targetType);
    setSelectedPrefecture('');
    setSelectedCity('');
    setSearchQuery('');
    setIsSearchOpen(false);
  };

  const getDisplayName = (geo: GeoTarget) => {
    return getFullAreaName(geo, geoTargets, true);
  };

  const handleSelectGeo = (geo: GeoTarget) => {
    if (geo.target_type === 'Prefecture') {
      setSelectedPrefecture(geo.id);
      setSelectedCity('');
    } else {
      setSelectedCity(geo.id);
      if (geo.parent_id) {
        setSelectedPrefecture(geo.parent_id);
      }
    }
    setSearchQuery('');
    setIsSearchOpen(false);
  };

  return (
    <div className={styles.geoSelector} ref={containerRef}>
      <div className={styles.searchRow}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="エリア名で検索 (例: 東京, Shibuya)"
          value={searchQuery}
          onFocus={() => setIsSearchOpen(true)}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {isSearchOpen && searchQuery.trim() && (
          <div className={styles.searchResultsPanel}>
            {rankedSearchResults.length > 0 ? (
              rankedSearchResults.map((geo) => (
                <button
                  key={geo.id}
                  type="button"
                  className={styles.searchResultItem}
                  onClick={() => handleSelectGeo(geo)}
                >
                  <span className={styles.searchResultLabel}>
                    {getFullAreaName(geo, geoTargets, false)}
                  </span>
                  <span className={styles.searchResultType}>
                    {geo.target_type === 'Prefecture' ? '都道府県' : '市区町村'}
                  </span>
                </button>
              ))
            ) : (
              <div className={styles.searchResultEmpty}>
                該当するエリアが見つかりません
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.dropdownRow}>
        <select
          className={styles.selectField}
          value={selectedPrefecture}
          onChange={(e) => {
            setSelectedPrefecture(e.target.value);
            setSelectedCity('');
            setIsSearchOpen(false);
          }}
        >
          <option value="">-- 都道府県を選択 --</option>
          {displayPrefectures.map(p => (
            <option key={p.id} value={p.id}>
              {getDisplayName(p)}
            </option>
          ))}
        </select>

        <select
          className={styles.selectField}
          value={selectedCity}
          onChange={(e) => {
            setSelectedCity(e.target.value);
            setIsSearchOpen(false);
          }}
          disabled={!selectedPrefecture && !searchQuery}
        >
          <option value="">-- 市区町村を選択 --</option>
          {displayCities.map(c => (
            <option key={c.id} value={c.id}>
              {getDisplayName(c)}
            </option>
          ))}
        </select>

        <select
          className={styles.selectFieldType}
          value={targetType}
          onChange={(e) => setTargetType(e.target.value as AreaTargetType)}
        >
          <option value="INCLUDE">対象 (INCLUDE)</option>
          <option value="EXCLUDE">除外 (EXCLUDE)</option>
        </select>

        <button
          type="button"
          className={styles.addButton}
          onClick={handleAdd}
          disabled={!selectedPrefecture && !selectedCity}
        >
          追加
        </button>
      </div>
    </div>
  );
};
